import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type ConsentPreferences = {
  necessary: true;
  analytics: boolean;
  externalMedia: boolean;
  advertising: boolean;
};

type StoredConsent = {
  version: 1;
  updatedAt: string;
  preferences: ConsentPreferences;
};

type ConsentContextValue = {
  preferences: ConsentPreferences;
  hasDecision: boolean;
  isPreferencesOpen: boolean;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  savePreferences: (preferences: Omit<ConsentPreferences, 'necessary'>) => void;
  openPreferences: () => void;
  closePreferences: () => void;
};

const STORAGE_KEY = 'sunny-cookie-consent';
const CONSENT_EVENT = 'sunny-consent-changed';
const defaultPreferences: ConsentPreferences = {
  necessary: true,
  analytics: false,
  externalMedia: false,
  advertising: false,
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

function readStoredConsent(): StoredConsent | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (!value) return null;
    const parsed = JSON.parse(value) as StoredConsent;
    return parsed.version === 1 && parsed.preferences ? parsed : null;
  } catch {
    return null;
  }
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [storedConsent, setStoredConsent] = useState<StoredConsent | null>(() => readStoredConsent());
  const [isPreferencesOpen, setPreferencesOpen] = useState(false);

  const persist = (preferences: ConsentPreferences) => {
    const next: StoredConsent = {
      version: 1,
      updatedAt: new Date().toISOString(),
      preferences,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setStoredConsent(next);
    setPreferencesOpen(false);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: preferences }));
  };

  useEffect(() => {
    const globalPrivacyControl = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl;
    if (!storedConsent && globalPrivacyControl) {
      persist(defaultPreferences);
    }
  }, []);

  const value = useMemo<ConsentContextValue>(() => ({
    preferences: storedConsent?.preferences ?? defaultPreferences,
    hasDecision: Boolean(storedConsent),
    isPreferencesOpen,
    acceptAll: () => persist({
      necessary: true,
      analytics: true,
      externalMedia: true,
      advertising: true,
    }),
    rejectNonEssential: () => persist(defaultPreferences),
    savePreferences: (preferences) => persist({ necessary: true, ...preferences }),
    openPreferences: () => setPreferencesOpen(true),
    closePreferences: () => setPreferencesOpen(false),
  }), [isPreferencesOpen, storedConsent]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const context = useContext(ConsentContext);
  if (!context) throw new Error('useConsent must be used within ConsentProvider');
  return context;
}

